import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

import {
  fetchAddPaymentMethod,
  fetchEditPaymentMethod,
  fetchGetPaymentMethods,
} from 'api/paymentApi';
import showSnackbarError from 'utils/showSnackbarError';

import { openBookedDialog } from './bookedDialogSlice';

const initialState = {
  isLoading: false,
  isLoaded: false,
};

const paymentMethodsAdapter = createEntityAdapter({
  selectId: (paymentMethod) => paymentMethod.id,
});

export const getPaymentMethods = createAsyncThunk(
  'paymentMethods/getPaymentMethods',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchGetPaymentMethods();

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { paymentMethods } = getState();

      if (paymentMethods.ids.length > 0) {
        return false;
      }

      return true;
    },
  }
);

export const addPaymentMethod = createAsyncThunk(
  'paymentMethods/addPaymentMethod',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchAddPaymentMethod(data);

      return response;
    } catch (e) {
      if (
        e?.response?.data?.statusCode === 400 &&
        e?.response?.data?.errors[0]
      ) {
        try {
          const { auth_msg: authMsg, rawType, raw } = e.response.data.errors[0];

          if (authMsg === 'A/DECLINED' || authMsg === 'Unknown') {
            dispatch(showSnackbarError('Invalid Card Data'));
          } else if (rawType === 'card_error' && raw.message) {
            dispatch(showSnackbarError(raw.message));
          } else {
            dispatch(showSnackbarError(`Error message: ${authMsg}`));
          }
        } catch (error) {
          dispatch(showSnackbarError(e.message, e.stack));
        }
      } else if (
        e?.response?.data?.statusCode === 409 &&
        e?.response?.data?.errors[0]?.type === 'stripe'
      ) {
        dispatch(
          showSnackbarError(
            e?.response?.data?.errors[0]?.why?.message ||
              'Credit Card Payment Failed. Please try again'
          )
        );
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const editPaymentMethod = createAsyncThunk(
  'paymentMethods/editPaymentMethod',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await fetchEditPaymentMethod(data);

      return {
        id: data.id,
        changes: {
          signature: data.signature,
        },
      };
    } catch (e) {
      if (
        e?.response?.data?.statusCode === 409 &&
        e?.response?.data?.errors[0]?.booked_until
      ) {
        dispatch(
          openBookedDialog({
            bookedUntilDate: e.response.data.errors[0].booked_until,
          })
        );
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const paymentMethodsSlice = createSlice({
  name: 'paymentMethods',
  initialState: paymentMethodsAdapter.getInitialState(initialState),
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentMethods.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        paymentMethodsAdapter.addMany(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(getPaymentMethods.rejected, (state) => {
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(addPaymentMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addPaymentMethod.fulfilled, (state, action) => {
        paymentMethodsAdapter.addOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(addPaymentMethod.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(editPaymentMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(editPaymentMethod.fulfilled, (state, action) => {
        paymentMethodsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(editPaymentMethod.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

const { selectAll, selectById } = paymentMethodsAdapter.getSelectors(
  (state) => state.paymentMethods
);

const all = createSelector(selectAll, (state) => state);

const byId = (id) =>
  createSelector(
    (state) => state,
    (state) => selectById(state, id)
  );

const selectSelf = (state) => state.paymentMethods;

const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isLoaded = createSelector(selectSelf, (state) => state.isLoaded);

export const PaymentMethodsSelectors = {
  all,
  byId,
  isLoading,
  isLoaded,
};

export default paymentMethodsSlice.reducer;
